import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LogOut, DollarSign, Package, TrendingUp, TrendingDown, Calculator, Eye, User, Mail, Phone, Calendar, Clock, MapPin, Heart, Briefcase, CheckCircle, CheckCircle2, XCircle, AlertCircle, Plus, Trash2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';

interface Order {
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  birth_date: string;
  birth_time: string;
  birth_place: string;
  marital_status: string | null;
  employment_status: string | null;
  service_id: string;
  service_title: string;
  service_price: string;
  price_numeric: number;
  partner_first_name: string | null;
  partner_last_name: string | null;
  partner_birth_date: string | null;
  partner_birth_time: string | null;
  partner_birth_place: string | null;
  questions: any;
  message: string | null;
  payment_status: 'pending' | 'paid' | 'cancelled';
  payment_confirmed_at: string | null;
  analysis_status: 'waiting' | 'in_progress' | 'completed' | 'delivered';
  analysis_started_at?: string | null;
  analysis_completed_at?: string | null;
  analysis_delivered_at?: string | null;
  deadline_date: string | null;
  admin_notes: string | null;
  discount_code: string | null;
  discount_percentage: number | null;
  original_price: number | null;
  final_price: number | null;
}

interface Expense {
  id: string;
  created_at: string;
  expense_date: string;
  category: string;
  description: string;
  amount: number;
}

interface DiscountCode {
  id: string;
  email: string;
  session_id: string;
  prize_type: string;
  discount_code: string;
  spin_date: string;
  is_used: boolean;
  used_at: string | null;
  order_id: string | null;
  created_at: string;
}

interface EmailListItem {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  birth_date: string | null;
  source: string;
  subscribed: boolean;
  created_at: string;
}

interface Testimonial {
  id: string;
  name: string;
  email: string | null;
  rating: number;
  comment: string;
  service_type: string | null;
  is_approved: boolean;
  is_featured: boolean;
  created_at: string;
}

interface ForumTransaction {
  id: string;
  user_id: string;
  email: string;
  package: string;
  amount: number;
  credits_added: number;
  payment_method: string;
  stripe_payment_id: string; // Reference number
  status: 'pending' | 'completed' | 'refunded';
  created_at: string;
}

interface BirthdayReminder {
  email: string;
  first_name: string | null;
  last_name: string | null;
  birth_date: string;
  source: string;
  birth_day: number;
  birth_month: number;
}


export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [emailList, setEmailList] = useState<EmailListItem[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [birthdays, setBirthdays] = useState<BirthdayReminder[]>([]);
  const [forumTransactions, setForumTransactions] = useState<ForumTransaction[]>([]);
  const [forumQuestions, setForumQuestions] = useState<any[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [answerText, setAnswerText] = useState('');
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [forumStatusFilter, setForumStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  
  // Statistika poseta
  const [visitStats, setVisitStats] = useState({
    today: 0,
    yesterday: 0,
    total: 0,
    thisWeek: 0,
    thisMonth: 0
  });
  
  // Novi trošak form
  const [newExpense, setNewExpense] = useState({
    date: new Date().toISOString().split('T')[0],
    category: 'reklame',
    description: '',
    amount: ''
  });

// Učitavanje podataka
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
      // Auto-refresh je uklonjen da ne prekida pisanje odgovora
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Učitaj porudžbine
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders_2026_03_19')
        .select('*')
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;
      setOrders(ordersData || []);

      // Učitaj troškove
      const { data: expensesData, error: expensesError } = await supabase
        .from('expenses_2026_03_19')
        .select('*')
        .order('expense_date', { ascending: false });

if (expensesError) throw expensesError;
      setExpenses(expensesData || []);

      // Učitaj discount kodove
      const { data: codesData, error: codesError } = await supabase
        .from('wheel_spins_2026_03_20')
        .select('*')
        .order('created_at', { ascending: false });

      if (codesError) throw codesError;
      setDiscountCodes(codesData || []);

      // Učitaj email listu
      const { data: emailData, error: emailError } = await supabase
        .from('email_list_2026_03_20')
        .select('*')
        .order('created_at', { ascending: false });

      if (emailError) throw emailError;
      setEmailList(emailData || []);

      // Učitaj testimonials
      console.log('Loading testimonials...');
      const { data: testimonialsData, error: testimonialsError } = await supabase
        .from('testimonials_2026_03_20')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Testimonials loaded:', testimonialsData);
      console.log('Testimonials error:', testimonialsError);
      if (testimonialsError) throw testimonialsError;
      setTestimonials(testimonialsData || []);

      // Učitaj forum transakcije
      const { data: forumTxData, error: forumTxError } = await supabase
        .from('credit_transactions_2026_03_28')
        .select('*')
        .order('created_at', { ascending: false });

      if (forumTxError) throw forumTxError;
      setForumTransactions(forumTxData || []);

      // Učitaj forum pitanja
      const { data: questionsData, error: questionsError } = await supabase
        .from('forum_questions_2026_03_28')
        .select('*')
        .order('created_at', { ascending: false });

      if (questionsError) throw questionsError;
      setForumQuestions(questionsData || []);
      // Učitaj rođendanske podseтnike
      const { data: birthdaysData, error: birthdaysError } = await supabase
        .from('birthday_reminders')
        .select('*');

      if (birthdaysError) console.error('Birthday reminders error:', birthdaysError);
      setBirthdays(birthdaysData || []);

      // Učitaj statistiku poseta
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
      const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

      // Posete danas
      const { data: todayVisits } = await supabase
        .from('visit_stats_2026_03_19')
        .select('total_visits')
        .eq('visit_date', today)
        .single();

      // Posete juče
      const { data: yesterdayVisits } = await supabase
        .from('visit_stats_2026_03_19')
        .select('total_visits')
        .eq('visit_date', yesterday)
        .single();

      // Ukupne posete
      const { data: allVisits } = await supabase
        .from('visit_stats_2026_03_19')
        .select('total_visits');

      // Posete ove nedelje
      const { data: weekVisits } = await supabase
        .from('visit_stats_2026_03_19')
        .select('total_visits')
        .gte('visit_date', weekAgo);

      // Posete ovog meseca
      const { data: monthVisits } = await supabase
        .from('visit_stats_2026_03_19')
        .select('total_visits')
        .gte('visit_date', monthAgo);

      setVisitStats({
        today: todayVisits?.total_visits || 0,
        yesterday: yesterdayVisits?.total_visits || 0,
        total: allVisits?.reduce((sum, v) => sum + v.total_visits, 0) || 0,
        thisWeek: weekVisits?.reduce((sum, v) => sum + v.total_visits, 0) || 0,
        thisMonth: monthVisits?.reduce((sum, v) => sum + v.total_visits, 0) || 0
      });

    } catch (error) {
      console.error('Greška pri učitavanju:', error);
    } finally {
      setLoading(false);
    }
  };

  // Potvrda forum uplate
  const confirmForumPayment = async (transaction: ForumTransaction) => {
    if (!confirm(`Potvrditi uplatu za ${transaction.email}?\nIznos: ${transaction.amount} RSD\nKrediti: ${transaction.credits_added}`)) {
      return;
    }

    try {
      // 1. Update transaction status
      const { error: txError } = await supabase
        .from('credit_transactions_2026_03_28')
        .update({ status: 'completed' })
        .eq('id', transaction.id);

      if (txError) throw txError;

      // 2. Check if user already has credits
      const { data: existingCredits } = await supabase
        .from('user_credits_2026_03_28')
        .select('*')
        .eq('user_id', transaction.user_id)
        .single();

      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30);

      if (existingCredits) {
        // Update existing credits
        const { error: updateError } = await supabase
          .from('user_credits_2026_03_28')
          .update({
            credits_remaining: existingCredits.credits_remaining + transaction.credits_added,
            credits_purchased: existingCredits.credits_purchased + transaction.credits_added,
            purchase_date: new Date().toISOString(),
            expiry_date: expiryDate.toISOString(),
            is_expired: false,
          })
          .eq('user_id', transaction.user_id);

        if (updateError) throw updateError;
      } else {
        // Create new credits record
        const { error: insertError } = await supabase
          .from('user_credits_2026_03_28')
          .insert({
            user_id: transaction.user_id,
            email: transaction.email,
            credits_remaining: transaction.credits_added,
            credits_purchased: transaction.credits_added,
            purchase_date: new Date().toISOString(),
            expiry_date: expiryDate.toISOString(),
            is_expired: false,
          });

        if (insertError) throw insertError;
      }

      alert('✅ Uplata potvrđena! Krediti su dodati. Kliknite "🔄 Osveži" da vidite promene.');
      // Ne pozivamo loadData() automatski
    } catch (error: any) {
      console.error('Error confirming payment:', error);
      alert('❌ Greška: ' + error.message);
    }
  };

  // Brisanje forum transakcije
  const deleteForumTransaction = async (transactionId: string) => {
    if (!confirm('Da li ste sigurni da želite da obrišete ovu transakciju?')) {
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('admin_delete_transaction_2026_03_29', {
        body: { transactionId }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      alert('✅ Transakcija obrisana! Kliknite "🔄 Osveži" da vidite promene.');
      // Ne pozivamo loadData() automatski
    } catch (error: any) {
      console.error('Error deleting transaction:', error);
      alert('❌ Greška: ' + error.message);
    }
  };

  // Pregled korisničkih detalja
  const viewUserDetails = async (email: string, userId: string) => {
    try {
      // Load user credits
      const { data: credits } = await supabase
        .from('user_credits_2026_03_28')
        .select('*')
        .eq('user_id', userId)
        .single();

      // Load all transactions
      const { data: transactions } = await supabase
        .from('credit_transactions_2026_03_28')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      // Load credit usage
      const { data: usage } = await supabase
        .from('credit_usage_2026_03_28')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      // Load questions
      const { data: questions } = await supabase
        .from('forum_questions_2026_03_28')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      setUserDetails({
        email,
        credits,
        transactions,
        usage,
        questions
      });
      setSelectedUserEmail(email);
    } catch (error: any) {
      console.error('Error loading user details:', error);
      alert('❌ Greška: ' + error.message);
    }
  };

  // Odgovori na pitanje
  const answerQuestion = async () => {
    if (!selectedQuestion || !answerText.trim()) {
      alert('Molimo unesite odgovor');
      return;
    }

    try {
      // Use Edge Function with service role key to bypass RLS
      const { data, error } = await supabase.functions.invoke('admin_answer_question_2026_03_29', {
        body: { 
          questionId: selectedQuestion.id,
          answer: answerText.trim()
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      alert('✅ Odgovor poslat! Kliknite "🔄 Osveži" da vidite promene.');
      setSelectedQuestion(null);
      setAnswerText('');
      // Ne pozivamo loadData() automatski da ne prekinemo druge admina koji pišu odgovore
    } catch (error: any) {
      console.error('Error answering question:', error);
      alert('❌ Greška: ' + error.message);
    }
  };

  // Potvrda uplate
  const confirmPayment = async (orderId: string) => {
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + 5);

    const { error } = await supabase
      .from('orders_2026_03_19')
      .update({
        payment_status: 'paid',
        payment_confirmed_at: new Date().toISOString(),
        deadline_date: deadline.toISOString()
      })
      .eq('id', orderId);

    if (!error) {
      alert('✅ Uplata potvrđena! Kliknite "🔄 Osveži" da vidite promene.');
    }
  };

  // Promena statusa analize
  const updateAnalysisStatus = async (orderId: string, status: string) => {
    const updates: any = { analysis_status: status };
    
    if (status === 'in_progress' && !orders.find(o => o.id === orderId)?.analysis_started_at) {
      updates.analysis_started_at = new Date().toISOString();
    }
    if (status === 'completed') {
      updates.analysis_completed_at = new Date().toISOString();
    }
    if (status === 'delivered') {
      updates.analysis_delivered_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('orders_2026_03_19')
      .update(updates)
      .eq('id', orderId);

    if (!error) {
      alert('✅ Status promenjen! Kliknite "🔄 Osveži" da vidite promene.');
    }
  };

  // Dodavanje troška
  const addExpense = async () => {
    if (!newExpense.description || !newExpense.amount) {
      alert('Popunite sva polja');
      return;
    }

    const { error } = await supabase
      .from('expenses_2026_03_19')
      .insert({
        expense_date: newExpense.date,
        category: newExpense.category,
        description: newExpense.description,
        amount: parseFloat(newExpense.amount)
      });

    if (!error) {
      setNewExpense({
        date: new Date().toISOString().split('T')[0],
        category: 'reklame',
        description: '',
        amount: ''
      });
      alert('✅ Trošak dodat! Kliknite "🔄 Osveži" da vidite promene.');
    }
  };

  // Brisanje troška
  const deleteExpense = async (id: string) => {
    if (confirm('Da li ste sigurni da želite da obrišete ovaj trošak?')) {
      const { error } = await supabase
        .from('expenses_2026_03_19')
        .delete()
        .eq('id', id);

      if (!error) {
        alert('✅ Trošak obrisan! Kliknite "🔄 Osveži" da vidite promene.');
      }
    }
};

  // Brisanje porudžbine
  const deleteOrder = async (id: string) => {
    if (confirm('Da li ste sigurni da želite da obrišete ovu porudžbinu? Ova akcija se ne može poništiti.')) {
      const { error } = await supabase
        .from('orders_2026_03_19')
        .delete()
        .eq('id', id);

      if (!error) {
        setSelectedOrder(null);
        alert('✅ Porudžbina obrisana! Kliknite "🔄 Osveži" da vidite promene.');
      }
    }
  };

  // Testimonials funkcije
  const approveTestimonial = async (id: string) => {
    const { error } = await supabase
      .from('testimonials_2026_03_20')
      .update({ is_approved: true })
      .eq('id', id);

    if (!error) alert('✅ Komentar odobren! Kliknite "🔄 Osveži" da vidite promene.');
  };

  const deleteTestimonial = async (id: string) => {
    if (confirm('Da li ste sigurni da želite da obrišete ovaj komentar?')) {
      const { error } = await supabase
        .from('testimonials_2026_03_20')
        .delete()
        .eq('id', id);

      if (!error) alert('✅ Komentar obrisan! Kliknite "🔄 Osveži" da vidite promene.');
    }
  };

  // Ažuriranje videa
  // Brisanje discount koda
  const deleteDiscountCode = async (id: string) => {
    if (confirm('Da li ste sigurni da želite da obrišete ovaj kod?')) {
      const { error } = await supabase
        .from('wheel_spins_2026_03_20')
        .delete()
        .eq('id', id);

      if (!error) alert('✅ Kod obrisan! Kliknite "🔄 Osveži" da vidite promene.');
    }
  };

  // Kalkulacije
  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(o => o.payment_status === filterStatus);

  const totalRevenue = orders
    .filter(o => o.payment_status === 'paid')
    .reduce((sum, o) => sum + (o.price_numeric || 0), 0);

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const netProfit = totalRevenue - totalExpenses;

  const pendingOrders = orders.filter(o => o.payment_status === 'pending').length;
  const paidOrders = orders.filter(o => o.payment_status === 'paid').length;

  // Login forma
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-primary/20 shadow-xl">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <User className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Admin Panel</CardTitle>
              <CardDescription>Astro Aries Studio</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => {
                e.preventDefault();
                if (password === 'Admin123') {
                  setIsAuthenticated(true);
                } else {
                  alert('Pogrešna lozinka!');
                }
              }}>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="password">Lozinka</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Unesite lozinku"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <Button type="submit" className="w-full" size="lg">
                    Prijavi se
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Učitavanje...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Admin Panel</h1>
            <p className="text-sm text-muted-foreground">Astro Aries Studio</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              size="sm"
              onClick={() => {
                loadData();
                alert('✅ Podaci osveženi!');
              }}
              disabled={loading}
              className="hidden sm:flex"
            >
              {loading ? 'Učitavanje...' : '🔄 Osveži'}
            </Button>
            <Button 
              variant="outline"
              size="icon"
              onClick={() => {
                loadData();
                alert('✅ Podaci osveženi!');
              }}
              disabled={loading}
              className="sm:hidden"
            >
              🔄
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(ROUTE_PATHS.HOME)} className="hidden sm:flex">
              Nazad na sajt
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setIsAuthenticated(false)}>
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Odjavi se</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Kalkulator - Glavne Statistike */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Finansijski Pregled
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Ukupna Zarada */}
            <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-green-700 dark:text-green-400 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Ukupna Zarada
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl sm:text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400">
                  {totalRevenue.toLocaleString()} RSD
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {paidOrders} plaćenih porudžbina
                </p>
              </CardContent>
            </Card>

            {/* Troškovi */}
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-red-700 dark:text-red-400 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4" />
                  Ukupni Troškovi
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl sm:text-3xl font-bold text-red-600 dark:text-red-400">
                  {totalExpenses.toLocaleString()} RSD
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {expenses.length} stavki
                </p>
              </CardContent>
            </Card>

            {/* Neto Profit */}
            <Card className={`border-2 ${netProfit >= 0 ? 'border-blue-200 bg-blue-50 dark:bg-blue-950/20' : 'border-orange-200 bg-orange-50 dark:bg-orange-950/20'}`}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-sm font-medium flex items-center gap-2 ${netProfit >= 0 ? 'text-blue-700 dark:text-blue-400' : 'text-orange-700 dark:text-orange-400'}`}>
                  <DollarSign className="w-4 h-4" />
                  Neto Profit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl sm:text-3xl font-bold ${netProfit >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'}`}>
                  {netProfit.toLocaleString()} RSD
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Zarada - Troškovi
                </p>
              </CardContent>
            </Card>

            {/* Porudžbine na čekanju */}
            <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-yellow-700 dark:text-yellow-400 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Na Čekanju
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl sm:text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                  {pendingOrders}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Neplaćene porudžbine
                </p>
              </CardContent>
            </Card>
          </div>
</div>

        {/* Statistika Poseta */}
        <div className="mb-8">
<div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Statistika Poseta
            </h2>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              Real-time (ažurira se svakih 30s)
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Posete Danas */}
            <Card className="border-purple-200 bg-purple-50 dark:bg-purple-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-400 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Danas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {visitStats.today}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Poseta danas
                </p>
              </CardContent>
            </Card>

            {/* Posete Juče */}
            <Card className="border-indigo-200 bg-indigo-50 dark:bg-indigo-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-indigo-700 dark:text-indigo-400 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Juče
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                  {visitStats.yesterday}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Poseta juče
                </p>
              </CardContent>
            </Card>

            {/* Ove Nedelje */}
            <Card className="border-cyan-200 bg-cyan-50 dark:bg-cyan-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-cyan-700 dark:text-cyan-400 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Ove Nedelje
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl sm:text-3xl font-bold text-cyan-600 dark:text-cyan-400">
                  {visitStats.thisWeek}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Poslednjih 7 dana
                </p>
              </CardContent>
            </Card>

            {/* Ovog Meseca */}
            <Card className="border-teal-200 bg-teal-50 dark:bg-teal-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-teal-700 dark:text-teal-400 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Ovog Meseca
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl sm:text-3xl font-bold text-teal-600 dark:text-teal-400">
                  {visitStats.thisMonth}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Poslednjih 30 dana
                </p>
              </CardContent>
            </Card>

            {/* Ukupno */}
            <Card className="border-slate-200 bg-slate-50 dark:bg-slate-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-700 dark:text-slate-400 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Ukupno
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl sm:text-3xl font-bold text-slate-600 dark:text-slate-400">
                  {visitStats.total}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Sve posete
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        <Tabs defaultValue="orders" className="space-y-6">
<TabsList className="flex flex-wrap gap-2 h-auto bg-transparent p-2">
            <TabsTrigger value="orders" className="flex-shrink-0">
              <span className="hidden sm:inline">Porudžbine</span>
              <span className="sm:hidden">📦</span>
              <span className="ml-1">({orders.length})</span>
            </TabsTrigger>
            <TabsTrigger value="forum" className="flex-shrink-0">
              <span className="hidden sm:inline">Transakcije</span>
              <span className="sm:hidden">💳</span>
              <span className="ml-1">({forumTransactions.filter(t => t.status === 'pending').length})</span>
            </TabsTrigger>
            <TabsTrigger value="forum-questions" className="flex-shrink-0">
              <span className="hidden sm:inline">Pitanja</span>
              <span className="sm:hidden">💬</span>
              <span className="ml-1">({forumQuestions.filter(q => q.status === 'pending').length})</span>
            </TabsTrigger>
            <TabsTrigger value="codes" className="flex-shrink-0">
              <span className="hidden sm:inline">Kodovi</span>
              <span className="sm:hidden">🎰</span>
              <span className="ml-1">({discountCodes.length})</span>
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex-shrink-0">
              <span className="hidden sm:inline">Email Lista</span>
              <span className="sm:hidden">✉️</span>
              <span className="ml-1">({emailList.length})</span>
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="flex-shrink-0">
              <span className="hidden sm:inline">Komentari</span>
              <span className="sm:hidden">⭐</span>
              <span className="ml-1">({testimonials.length})</span>
            </TabsTrigger>
            <TabsTrigger value="birthdays" className="flex-shrink-0">
              <span className="hidden sm:inline">Rođendani</span>
              <span className="sm:hidden">🎂</span>
              <span className="ml-1">({birthdays.length})</span>
            </TabsTrigger>
            <TabsTrigger value="expenses" className="flex-shrink-0">
              <span className="hidden sm:inline">Troškovi</span>
              <span className="sm:hidden">💸</span>
              <span className="ml-1">({expenses.length})</span>
            </TabsTrigger>
            <TabsTrigger value="visits" className="flex-shrink-0">
              <span className="hidden sm:inline">Posete</span>
              <span className="sm:hidden">👁️</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab: Porudžbine */}
          <TabsContent value="orders" className="space-y-4">
            {/* Filter */}
            <div className="flex items-center gap-4">
              <Label>Filter:</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Sve ({orders.length})</SelectItem>
                  <SelectItem value="pending">Na čekanju ({pendingOrders})</SelectItem>
                  <SelectItem value="paid">Plaćeno ({paidOrders})</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tabela Porudžbina */}
            <Card>
              <CardHeader>
                <CardTitle>Sve Porudžbine</CardTitle>
                <CardDescription>Kliknite na red za detalje</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Klijent</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Usluga</TableHead>
                        <TableHead>Cena</TableHead>
                        <TableHead>Status Uplate</TableHead>
                        <TableHead>Status Analize</TableHead>
                        <TableHead>Deadline</TableHead>
                        <TableHead>Akcije</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOrders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                            Nema porudžbina
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredOrders.map((order) => (
                          <TableRow 
                            key={order.id} 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => setSelectedOrder(order)}
                          >
                            <TableCell className="font-medium">
                              {new Date(order.created_at).toLocaleDateString('sr-RS')}
                            </TableCell>
                            <TableCell>{order.first_name} {order.last_name}</TableCell>
                            <TableCell className="text-sm">{order.email}</TableCell>
                            <TableCell className="text-sm">{order.service_title}</TableCell>
                            <TableCell className="font-semibold">
                              {order.discount_code ? (
                                <div className="flex flex-col gap-1">
                                  <span className="line-through text-muted-foreground text-sm">{order.original_price?.toLocaleString()} RSD</span>
                                  <span className="text-green-600 font-bold">{order.final_price?.toLocaleString()} RSD</span>
                                  <span className="text-xs text-orange-600">-{order.discount_percentage}% ({order.discount_code})</span>
                                </div>
                              ) : (
                                <span>{order.price_numeric.toLocaleString()} RSD</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {order.payment_status === 'pending' && (
                                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                                  Na čekanju
                                </Badge>
                              )}
                              {order.payment_status === 'paid' && (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  Plaćeno
                                </Badge>
                              )}
                              {order.payment_status === 'cancelled' && (
                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                  Otkazano
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {order.analysis_status === 'waiting' && <Badge variant="secondary">Čeka</Badge>}
                              {order.analysis_status === 'in_progress' && <Badge className="bg-blue-500">U toku</Badge>}
                              {order.analysis_status === 'completed' && <Badge className="bg-purple-500">Završeno</Badge>}
                              {order.analysis_status === 'delivered' && <Badge className="bg-green-500">Isporučeno</Badge>}
                            </TableCell>
                            <TableCell>
                              {order.deadline_date ? (
                                <span className="text-sm">
                                  {new Date(order.deadline_date).toLocaleDateString('sr-RS')}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {order.payment_status === 'pending' && (
                                  <Button 
                                    size="sm" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      confirmPayment(order.id);
                                    }}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Potvrdi
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Discount Kodovi */}
          {/* Tab: Forum Transakcije */}
          <TabsContent value="forum" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>💳 Forum Transakcije</CardTitle>
                <CardDescription>
                  Potvrda uplata za kupovinu kredita
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filter */}
                <div className="flex gap-4">
                  <Select value={forumStatusFilter} onValueChange={setForumStatusFilter}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Sve transakcije</SelectItem>
                      <SelectItem value="pending">⏳ Pending</SelectItem>
                      <SelectItem value="completed">✅ Potvrđeno</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Paket</TableHead>
                        <TableHead>Iznos</TableHead>
                        <TableHead>Krediti</TableHead>
                        <TableHead>Način Plaćanja</TableHead>
                        <TableHead>Poziv na broj</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Akcije</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {forumTransactions
                        .filter(tx => forumStatusFilter === 'all' || tx.status === forumStatusFilter)
                        .length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center text-muted-foreground">
                            Nema transakcija
                          </TableCell>
                        </TableRow>
                      ) : (
                        forumTransactions
                          .filter(tx => forumStatusFilter === 'all' || tx.status === forumStatusFilter)
                          .map((tx) => (
                          <TableRow key={tx.id}>
                            <TableCell>
                              {new Date(tx.created_at).toLocaleDateString('sr-RS', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="link"
                                className="font-mono text-sm p-0 h-auto"
                                onClick={() => viewUserDetails(tx.email, tx.user_id)}
                              >
                                {tx.email}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {tx.package === 'starter' && 'Starter'}
                                {tx.package === 'popularan' && 'Popularan'}
                                {tx.package === 'vip' && 'VIP'}
                                {tx.package === 'premium' && 'Premium'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-bold">
                              {tx.amount.toLocaleString('sr-RS')} RSD
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">
                                {tx.credits_added} kredita
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {tx.payment_method === 'ips_qr' && '📱 IPS QR'}
                              {tx.payment_method === 'bank_slip' && '📄 Uplatnica'}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {tx.stripe_payment_id}
                            </TableCell>
                            <TableCell>
                              {tx.status === 'pending' && (
                                <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                                  ⏳ Pending
                                </Badge>
                              )}
                              {tx.status === 'completed' && (
                                <Badge variant="secondary" className="bg-green-100 text-green-800">
                                  ✅ Potvrđeno
                                </Badge>
                              )}
                              {tx.status === 'refunded' && (
                                <Badge variant="destructive">
                                  ❌ Refund
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {tx.status === 'pending' && (
                                  <Button
                                    size="sm"
                                    onClick={() => confirmForumPayment(tx)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                    Potvrdi
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => deleteForumTransaction(tx.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Details Dialog */}
          <Dialog open={!!selectedUserEmail} onOpenChange={() => setSelectedUserEmail(null)}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>👤 Detalji Korisnika</DialogTitle>
                <DialogDescription>
                  {selectedUserEmail}
                </DialogDescription>
              </DialogHeader>

              {userDetails && (
                <div className="space-y-6">
                  {/* Credits Summary */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">💳 Krediti</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {userDetails.credits ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-sm text-muted-foreground">Preostalo</p>
                            <p className="text-2xl font-bold text-green-600">
                              {userDetails.credits.credits_remaining}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Kupljeno</p>
                            <p className="text-2xl font-bold">
                              {userDetails.credits.credits_purchased}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Potrošeno</p>
                            <p className="text-2xl font-bold text-red-600">
                              {userDetails.credits.credits_used}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Status</p>
                            <Badge variant={userDetails.credits.is_expired ? "destructive" : "secondary"}>
                              {userDetails.credits.is_expired ? '❌ Isteklo' : '✅ Aktivno'}
                            </Badge>
                          </div>
                          <div className="col-span-2">
                            <p className="text-sm text-muted-foreground">Datum kupovine</p>
                            <p className="font-mono text-sm">
                              {new Date(userDetails.credits.purchase_date).toLocaleDateString('sr-RS')}
                            </p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-sm text-muted-foreground">Ističe</p>
                            <p className="font-mono text-sm">
                              {new Date(userDetails.credits.expiry_date).toLocaleDateString('sr-RS')}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">Nema kredita</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Transactions */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">💰 Transakcije</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {userDetails.transactions && userDetails.transactions.length > 0 ? (
                        <div className="space-y-2">
                          {userDetails.transactions.map((tx: any) => (
                            <div key={tx.id} className="flex justify-between items-center p-3 border rounded">
                              <div>
                                <p className="font-semibold">{tx.package}</p>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(tx.created_at).toLocaleDateString('sr-RS', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold">{tx.amount} RSD</p>
                                <Badge variant={tx.status === 'completed' ? 'secondary' : 'outline'}>
                                  {tx.status === 'pending' && '⏳ Pending'}
                                  {tx.status === 'completed' && '✅ Potvrđeno'}
                                  {tx.status === 'refunded' && '❌ Refund'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">Nema transakcija</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Credit Usage */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">📊 Potrošnja Kredita</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {userDetails.usage && userDetails.usage.length > 0 ? (
                        <div className="space-y-2">
                          {userDetails.usage.map((u: any) => (
                            <div key={u.id} className="flex justify-between items-center p-3 border rounded">
                              <div>
                                <p className="font-semibold">{u.action_type}</p>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(u.created_at).toLocaleDateString('sr-RS', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              <div className="text-right">
                                <Badge variant="destructive">-{u.credits_used} kredita</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">Nema potrošnje</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Questions */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">❓ Pitanja</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {userDetails.questions && userDetails.questions.length > 0 ? (
                        <div className="space-y-3">
                          {userDetails.questions.map((q: any) => (
                            <div key={q.id} className="p-3 border rounded space-y-2">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <Badge variant="outline">{q.category}</Badge>
                                  <p className="mt-2 font-semibold">{q.question_text}</p>
                                </div>
                                <Badge variant={q.status === 'answered' ? 'secondary' : 'outline'}>
                                  {q.status === 'pending' && '⏳ Pending'}
                                  {q.status === 'answered' && '✅ Odgovoreno'}
                                </Badge>
                              </div>
                              {q.answer_text && (
                                <div className="mt-2 p-2 bg-muted rounded">
                                  <p className="text-sm">{q.answer_text}</p>
                                </div>
                              )}
                              <p className="text-xs text-muted-foreground">
                                {new Date(q.created_at).toLocaleDateString('sr-RS', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">Nema pitanja</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </DialogContent>
          </Dialog>


          {/* Tab: Forum Pitanja */}
          <TabsContent value="forum-questions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>💬 Forum Pitanja</CardTitle>
                <CardDescription>
                  Odgovorite na pitanja korisnika
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {forumQuestions.length > 0 ? (
                    forumQuestions.map((question) => (
                      <Card key={question.id} className={question.status === 'pending' ? 'border-yellow-500/50' : 'border-green-500/50'}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant={question.status === 'pending' ? 'outline' : 'default'}>
                                  {question.status === 'pending' ? '⏳ Na čekanju' : '✅ Odgovoreno'}
                                </Badge>
                                <Badge variant="secondary">{question.category}</Badge>
                              </div>
                              <CardTitle className="text-lg mb-2">{question.question}</CardTitle>
                              
                              {/* Birth Data */}
                              {(question.birth_date || question.birth_time || question.birth_place) && (
                                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground bg-muted/30 rounded-lg p-2 mb-2">
                                  {question.birth_date && (
                                    <div className="flex items-center gap-1">
                                      📅 <span>{new Date(question.birth_date).toLocaleDateString('sr-RS')}</span>
                                    </div>
                                  )}
                                  {question.birth_time && (
                                    <div className="flex items-center gap-1">
                                      🕐 <span>{question.birth_time}</span>
                                    </div>
                                  )}
                                  {question.birth_place && (
                                    <div className="flex items-center gap-1">
                                      📍 <span>{question.birth_place}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>👤 {question.display_name}</span>
                                <span>📧 {question.email}</span>
                                <span>🕐 {new Date(question.created_at).toLocaleDateString('sr-RS')}</span>
                              </div>
                            </div>
                            {question.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedQuestion(question);
                                  setAnswerText('');
                                }}
                              >
                                Odgovori
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                        {question.answer && (
                          <CardContent>
                            <div className="bg-muted/30 rounded-lg p-4 border-l-4 border-green-500">
                              <p className="text-sm font-medium mb-2">✅ Odgovor:</p>
                              <p className="text-sm whitespace-pre-wrap">{question.answer}</p>
                              <p className="text-xs text-muted-foreground mt-2">
                                Odgovoreno: {question.answered_at && new Date(question.answered_at).toLocaleDateString('sr-RS')}
                              </p>
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    ))
                  ) : (
                    <p className="text-center text-muted-foreground py-8">Nema pitanja</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dialog za odgovaranje */}
          <Dialog open={!!selectedQuestion} onOpenChange={(open) => !open && setSelectedQuestion(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Odgovori na pitanje</DialogTitle>
              </DialogHeader>
              {selectedQuestion && (
                <div className="space-y-4">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="font-medium mb-2">Pitanje:</p>
                    <p className="text-sm mb-4">{selectedQuestion.question}</p>
                    
                    {/* Birth Data */}
                    {(selectedQuestion.birth_date || selectedQuestion.birth_time || selectedQuestion.birth_place) && (
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground bg-background rounded-lg p-2 mb-2">
                        {selectedQuestion.birth_date && (
                          <div className="flex items-center gap-1">
                            📅 <span>{new Date(selectedQuestion.birth_date).toLocaleDateString('sr-RS')}</span>
                          </div>
                        )}
                        {selectedQuestion.birth_time && (
                          <div className="flex items-center gap-1">
                            🕐 <span>{selectedQuestion.birth_time}</span>
                          </div>
                        )}
                        {selectedQuestion.birth_place && (
                          <div className="flex items-center gap-1">
                            📍 <span>{selectedQuestion.birth_place}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>👤 {selectedQuestion.display_name}</span>
                      <span>📧 {selectedQuestion.email}</span>
                      <span>📂 {selectedQuestion.category}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="answer">Vaš odgovor:</Label>
                    <Textarea
                      id="answer"
                      value={answerText}
                      onChange={(e) => setAnswerText(e.target.value)}
                      rows={12}
                      placeholder="Unesite detaljan astrološki odgovor..."
                      className="resize-none"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setSelectedQuestion(null)}>
                      Otkaži
                    </Button>
                    <Button onClick={answerQuestion}>
                      Pošalji odgovor
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Tab: Discount Kodovi */}
          <TabsContent value="codes" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>🎰 Discount Kodovi - Slot Mašina</CardTitle>
                <CardDescription>
                  Pregled svih generisanih discount kodova iz Slot Mašine
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Kod</TableHead>
                        <TableHead>Nagrada</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Korišćen</TableHead>
                        <TableHead>Akcije</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {discountCodes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">
                            Nema generisanih kodova
                          </TableCell>
                        </TableRow>
                      ) : (
                        discountCodes.map((code) => (
                          <TableRow key={code.id}>
                            <TableCell>
                              {new Date(code.created_at).toLocaleDateString('sr-RS', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {code.email}
                            </TableCell>
                            <TableCell>
                              <code className="px-2 py-1 bg-muted rounded text-sm font-bold">
                                {code.discount_code}
                              </code>
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                code.prize_type === 'FREE' ? 'default' :
                                code.prize_type === '50%' ? 'destructive' :
                                code.prize_type === '30%' ? 'secondary' : 'outline'
                              }>
                                {code.prize_type === 'FREE' ? '🎁 BESPLATNO' : `💰 ${code.prize_type}`}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {code.is_used ? (
                                <Badge variant="secondary" className="bg-green-100 text-green-800">
                                  ✅ Iskorišćen
                                </Badge>
                              ) : (
                                <Badge variant="outline">
                                  ⏳ Neiskorišćen
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {code.used_at ? (
                                new Date(code.used_at).toLocaleDateString('sr-RS', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteDiscountCode(code.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Statistika */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Ukupno Kodova</CardDescription>
                      <CardTitle className="text-2xl">{discountCodes.length}</CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Iskorišćeno</CardDescription>
                      <CardTitle className="text-2xl text-green-600">
                        {discountCodes.filter(c => c.is_used).length}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Neiskorišćeno</CardDescription>
                      <CardTitle className="text-2xl text-orange-600">
                        {discountCodes.filter(c => !c.is_used).length}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Besplatno</CardDescription>
                      <CardTitle className="text-2xl text-purple-600">
                        {discountCodes.filter(c => c.prize_type === 'FREE').length}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                </div>

                {/* Distribucija nagrada */}
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>📊 Distribucija Nagrada</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span>🎁 BESPLATNO:</span>
                        <Badge>{discountCodes.filter(c => c.prize_type === 'FREE').length}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>💎 50% popust:</span>
                        <Badge>{discountCodes.filter(c => c.prize_type === '50%').length}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>💰 30% popust:</span>
                        <Badge>{discountCodes.filter(c => c.prize_type === '30%').length}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>⭐ 20% popust:</span>
                        <Badge>{discountCodes.filter(c => c.prize_type === '20%').length}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>🎯 10% popust:</span>
                        <Badge variant="secondary">{discountCodes.filter(c => c.prize_type === '10%').length}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Troškovi */}
          <TabsContent value="expenses" className="space-y-4">
            {/* Forma za dodavanje troška */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Dodaj Novi Trošak
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Datum</Label>
                    <Input 
                      type="date" 
                      value={newExpense.date}
                      onChange={(e) => setNewExpense({...newExpense, date: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Kategorija</Label>
                    <Select value={newExpense.category} onValueChange={(v) => setNewExpense({...newExpense, category: v})}>
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reklame">Reklame</SelectItem>
                        <SelectItem value="hosting">Hosting</SelectItem>
                        <SelectItem value="alati">Alati</SelectItem>
                        <SelectItem value="ostalo">Ostalo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Opis</Label>
                    <Input 
                      placeholder="Npr: Facebook Ads"
                      value={newExpense.description}
                      onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Iznos (RSD)</Label>
                    <div className="flex gap-2 mt-2">
                      <Input 
                        type="number"
                        placeholder="5000"
                        value={newExpense.amount}
                        onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                      />
                      <Button onClick={addExpense}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabela Troškova */}
            <Card>
              <CardHeader>
                <CardTitle>Svi Troškovi</CardTitle>
                <CardDescription>Ukupno: {totalExpenses.toLocaleString()} RSD</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Datum</TableHead>
                      <TableHead>Kategorija</TableHead>
                      <TableHead>Opis</TableHead>
                      <TableHead>Iznos</TableHead>
                      <TableHead>Akcije</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          Nema troškova
                        </TableCell>
                      </TableRow>
                    ) : (
                      expenses.map((expense) => (
                        <TableRow key={expense.id}>
                          <TableCell>{new Date(expense.expense_date).toLocaleDateString('sr-RS')}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{expense.category}</Badge>
                          </TableCell>
                          <TableCell>{expense.description}</TableCell>
                          <TableCell className="font-semibold">{expense.amount.toLocaleString()} RSD</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => deleteExpense(expense.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
</Card>
          </TabsContent>

          {/* Tab: Posete */}
          <TabsContent value="visits" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Statistika Poseta
                </CardTitle>
                <CardDescription>
                  Pregled poseta sajtu po danima
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Glavne Metrike */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200">
                      <p className="text-sm text-muted-foreground mb-1">Danas</p>
                      <p className="text-2xl font-bold text-purple-600">{visitStats.today}</p>
                    </div>
                    <div className="p-4 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg border border-indigo-200">
                      <p className="text-sm text-muted-foreground mb-1">Juče</p>
                      <p className="text-2xl font-bold text-indigo-600">{visitStats.yesterday}</p>
                    </div>
                    <div className="p-4 bg-cyan-50 dark:bg-cyan-950/20 rounded-lg border border-cyan-200">
                      <p className="text-sm text-muted-foreground mb-1">Ove Nedelje</p>
                      <p className="text-2xl font-bold text-cyan-600">{visitStats.thisWeek}</p>
                    </div>
                    <div className="p-4 bg-teal-50 dark:bg-teal-950/20 rounded-lg border border-teal-200">
                      <p className="text-sm text-muted-foreground mb-1">Ovog Meseca</p>
                      <p className="text-2xl font-bold text-teal-600">{visitStats.thisMonth}</p>
                    </div>
                  </div>

{/* Real-time Status */}
                  <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <h3 className="font-semibold text-green-700 dark:text-green-400">✅ Real-time Praćenje Aktivno!</h3>
                    </div>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>• <strong>Automatsko beleženje:</strong> Svaka poseta sajtu se automatski beleži u bazu</p>
                      <p>• <strong>Session tracking:</strong> Prati se svaki posetilac sa jedinstvenim session ID-om</p>
                      <p>• <strong>Detekcija izvora:</strong> Automatski detektuje odakle dolaze posetioci (Google, Facebook, direktno)</p>
                      <p>• <strong>Auto-refresh:</strong> Statistika se ažurira svakih 2 minuta</p>
                      <p>• <strong>Dnevna agregacija:</strong> Podaci se agregiraju po danima za lašku analizu</p>
                    </div>
                  </div>

                  {/* Ukupna Statistika */}
                  <div className="p-6 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-2">Ukupan Broj Poseta</p>
                      <p className="text-5xl font-bold text-primary">{visitStats.total.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground mt-2">Od početka praćenja</p>
                    </div>
                  </div>

{/* Kako Funkcioniše */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200">
                    <h3 className="font-semibold mb-2 text-blue-700 dark:text-blue-400">🔍 Kako Funkcioniše?</h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p><strong>1. Posetilac otvara sajt</strong> → Automatski se generiše jedinstveni session ID</p>
                      <p><strong>2. Tracking funkcija</strong> → Poziva se Edge funkcija koja beleži posetu</p>
                      <p><strong>3. Detekcija izvora</strong> → Analizira se referrer URL (Google, Facebook, direktno)</p>
                      <p><strong>4. Baza podataka</strong> → Podaci se čuvaju u dve tabele:</p>
                      <p className="ml-4">• <code className="bg-muted px-1 rounded">realtime_visits_2026_03_19</code> - Svaka pojedinačna poseta</p>
                      <p className="ml-4">• <code className="bg-muted px-1 rounded">visit_stats_2026_03_19</code> - Dnevna agregirana statistika</p>
                      <p><strong>5. Admin panel</strong> → Prikazuje statistiku sa auto-refresh-om svakih 30s</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Email Lista */}
          <TabsContent value="emails" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>📧 Email Lista</CardTitle>
                <CardDescription>
                  Svi prikupljeni email-ovi iz različitih izvora
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Ime</TableHead>
                        <TableHead>Izvor</TableHead>
                        <TableHead>Rođendan</TableHead>
                        <TableHead>Datum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emailList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            Nema email-ova u listi
                          </TableCell>
                        </TableRow>
                      ) : (
                        emailList.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-mono text-sm">{item.email}</TableCell>
                            <TableCell>
                              {item.first_name && item.last_name 
                                ? `${item.first_name} ${item.last_name}` 
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                item.source === 'order' ? 'default' :
                                item.source === 'wheel' || item.source === 'wheel_of_fortune' ? 'secondary' : 'outline'
                              }>
                                {item.source === 'order' ? '🛒 Porudžbina' :
                                 item.source === 'wheel' || item.source === 'wheel_of_fortune' ? '🎰 Točak Sreće' : item.source}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {item.birth_date 
                                ? new Date(item.birth_date).toLocaleDateString('sr-RS', {
                                    day: '2-digit',
                                    month: '2-digit'
                                  })
                                : '-'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(item.created_at).toLocaleDateString('sr-RS')}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Statistika */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Ukupno</CardDescription>
                      <CardTitle className="text-2xl">{emailList.length}</CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Iz Porudžbina</CardDescription>
                      <CardTitle className="text-2xl text-blue-600">
                        {emailList.filter(e => e.source === 'order').length}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Iz Slot Mašine</CardDescription>
                      <CardTitle className="text-2xl text-purple-600">
                        {emailList.filter(e => e.source === 'wheel').length}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Testimonials */}
          <TabsContent value="testimonials" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>💬 Komentari / Utisci</CardTitle>
                <CardDescription>
                  Upravljanje komentarima korisnika
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {testimonials.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      Nema komentara
                    </div>
                  ) : (
                    testimonials.map((testimonial) => (
                      <Card key={testimonial.id} className={testimonial.is_approved ? 'border-green-200' : 'border-orange-200'}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                              <CardDescription className="text-xs">
                                {testimonial.email || 'Bez email-a'} • {testimonial.service_type || 'Bez usluge'}
                              </CardDescription>
                              <div className="flex gap-1 mt-2">
                                {Array.from({ length: 5 }, (_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${
                                      i < testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                            <Badge variant={testimonial.is_approved ? 'default' : 'secondary'}>
                              {testimonial.is_approved ? '✅ Odobren' : '⏳ Na čekanju'}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-4 italic">"{testimonial.comment}"</p>
                          <div className="flex gap-2">
                            {!testimonial.is_approved && (
                              <Button
                                size="sm"
                                onClick={() => approveTestimonial(testimonial.id)}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Odobri
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteTestimonial(testimonial.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Obriši
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-4">
                            {new Date(testimonial.created_at).toLocaleString('sr-RS')}
                          </p>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                {/* Statistika */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Ukupno</CardDescription>
                      <CardTitle className="text-2xl">{testimonials.length}</CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Odobreno</CardDescription>
                      <CardTitle className="text-2xl text-green-600">
                        {testimonials.filter(t => t.is_approved).length}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Na čekanju</CardDescription>
                      <CardTitle className="text-2xl text-orange-600">
                        {testimonials.filter(t => !t.is_approved).length}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Rođendani */}
          <TabsContent value="birthdays" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>🎂 Rođendanski Podsетnici</CardTitle>
                <CardDescription>
                  Korisnici čiji je rođendan danas ili za 7 dana
                </CardDescription>
              </CardHeader>
              <CardContent>
                {birthdays.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    Nema rođendana u narednih 7 dana
                  </div>
                ) : (
                  <div className="space-y-4">
                    {birthdays.map((birthday, index) => (
                      <Card key={index} className="border-pink-200 bg-pink-50/50">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-lg">
                                {birthday.first_name && birthday.last_name 
                                  ? `${birthday.first_name} ${birthday.last_name}` 
                                  : birthday.email}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {birthday.email}
                              </p>
                              <p className="text-sm mt-2">
                                🎂 Rođendan: <strong>{birthday.birth_day}.{birthday.birth_month}.</strong>
                              </p>
                              <Badge variant="outline" className="mt-2">
                                {birthday.source === 'order' ? '🛒 Porudžbina' : '🎰 Slot'}
                              </Badge>
                            </div>
                            <Button
                              onClick={() => window.open(`mailto:${birthday.email}?subject=Srećan Rođendan!&body=Poštovani ${birthday.first_name || ''},\n\nŽelimo vam najlepše želje za vaš rođendan! 🎉🎂\n\nAstro Aries Studio`, '_blank')}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Pošalji Čestitku
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                <Alert className="mt-6">
                  <AlertDescription>
                    💡 <strong>Napomena:</strong> Ovde se prikazuju korisnici čiji je rođendan <strong>danas</strong> ili <strong>za 7 dana</strong>. Automatski se ažurira svaki dan.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>

      {/* Dialog: Detalji Porudžbine */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalji Porudžbine</DialogTitle>
            <DialogDescription>
              ID: {selectedOrder?.id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Podaci o Klijentu */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Podaci o Klijentu
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Ime i Prezime:</span>
                    <p className="font-medium">{selectedOrder.first_name} {selectedOrder.last_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      Email:
                    </span>
                    <p className="font-medium">{selectedOrder.email}</p>
                  </div>
                  {selectedOrder.phone && (
                    <div>
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        Telefon:
                      </span>
                      <p className="font-medium">{selectedOrder.phone}</p>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Podaci o Rođenju */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Podaci o Rođenju
                </h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Datum:</span>
                    <p className="font-medium">{selectedOrder.birth_date}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Vreme:
                    </span>
                    <p className="font-medium">{selectedOrder.birth_time}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Mesto:
                    </span>
                    <p className="font-medium">{selectedOrder.birth_place}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Lični Podaci */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Heart className="w-4 h-4" />
                  Lični Podaci
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedOrder.marital_status && (
                    <div>
                      <span className="text-muted-foreground">Bračni Status:</span>
                      <p className="font-medium">{selectedOrder.marital_status}</p>
                    </div>
                  )}
                  {selectedOrder.employment_status && (
                    <div>
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        Zaposlenje:
                      </span>
                      <p className="font-medium">{selectedOrder.employment_status}</p>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Usluga */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Usluga
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Naziv:</span>
                    <p className="font-medium">{selectedOrder.service_title}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Cena:</span>
                    {selectedOrder.discount_code ? (
                      <div className="flex flex-col gap-1">
                        <p className="line-through text-muted-foreground text-sm">{selectedOrder.original_price?.toLocaleString()} RSD</p>
                        <p className="font-bold text-xl text-green-600">{selectedOrder.final_price?.toLocaleString()} RSD</p>
                        <p className="text-xs text-orange-600 font-semibold">
                          Popust: -{selectedOrder.discount_percentage}% (Kod: {selectedOrder.discount_code})
                        </p>
                      </div>
                    ) : (
                      <p className="font-medium text-lg text-primary">{selectedOrder.price_numeric.toLocaleString()} RSD</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Podaci o Partneru (ako postoje) */}
              {selectedOrder.partner_first_name && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-3">💑 Podaci o Partneru (Sinastija)</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Ime i Prezime:</span>
                        <p className="font-medium">{selectedOrder.partner_first_name} {selectedOrder.partner_last_name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Datum Rođenja:</span>
                        <p className="font-medium">{selectedOrder.partner_birth_date}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Vreme:</span>
                        <p className="font-medium">{selectedOrder.partner_birth_time}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Mesto:</span>
                        <p className="font-medium">{selectedOrder.partner_birth_place}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Pitanja (ako postoje) */}
              {selectedOrder.questions && Object.keys(selectedOrder.questions).length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-3">❓ Pitanja Klijenta</h3>
                    <div className="space-y-2 text-sm">
                      {Object.entries(selectedOrder.questions).map(([key, value]) => (
                        <div key={key} className="p-3 bg-muted/50 rounded-lg">
                          <span className="font-medium">{key.replace('question', 'Pitanje ')}:</span>
                          <p className="mt-1">{value as string}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Dodatna Poruka */}
              {selectedOrder.message && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-3">📝 Dodatna Poruka</h3>
                    <p className="text-sm p-3 bg-muted/50 rounded-lg">{selectedOrder.message}</p>
                  </div>
                </>
              )}

              <Separator />

              {/* Status i Akcije */}
              <div>
                <h3 className="font-semibold mb-3">⚙️ Status i Akcije</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Status Uplate</Label>
                      <div className="mt-2">
                        {selectedOrder.payment_status === 'pending' ? (
                          <Button 
                            onClick={() => {
                              confirmPayment(selectedOrder.id);
                              setSelectedOrder(null);
                            }}
                            className="w-full"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Potvrdi Uplatu
                          </Button>
                        ) : (
                          <Badge className="bg-green-500 text-white">Plaćeno</Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label>Status Analize</Label>
                      <Select 
                        value={selectedOrder.analysis_status} 
                        onValueChange={(v) => {
                          updateAnalysisStatus(selectedOrder.id, v);
                          setSelectedOrder({...selectedOrder, analysis_status: v as any});
                        }}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="waiting">Čeka</SelectItem>
                          <SelectItem value="in_progress">U toku</SelectItem>
                          <SelectItem value="completed">Završeno</SelectItem>
                          <SelectItem value="delivered">Isporučeno</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {selectedOrder.deadline_date && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200">
                      <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                        ⏰ Deadline: {new Date(selectedOrder.deadline_date).toLocaleDateString('sr-RS')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

<DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => selectedOrder && deleteOrder(selectedOrder.id)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Obriši Porudžbinu
            </Button>
            <Button variant="outline" onClick={() => setSelectedOrder(null)}>
              Zatvori
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}